import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Card, Spinner, Form, Button, Row, Col, Modal } from "react-bootstrap";
import BackendHeader from "../component/BackendHeader";
import axiosInstance from "../axiosInstance";
import * as general from "../libs/general";
import { toast } from "react-toastify";
import { FiEdit2, FiSave } from "react-icons/fi";
import { API_URL_All } from "../api";
import "../css/AdminMyProfile.css";

const roleLabel = (profileRole, userFlags = {}) => {
  if (userFlags?.isAdmin === true) return "Quản trị viên";
  if (userFlags?.isMember === true) return "Thành viên nội bộ";
  if (profileRole === "user") return "Khách hàng";
  return profileRole || "—";
};

const resolveAvatarSrc = (picture, fallbackUserPath) => {
  const raw = picture || fallbackUserPath;
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const base = (API_URL_All || "").replace(/\/$/, "");
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
};

const emptyForm = {
  name: "",
  customerCode: "",
  phoneNumber: "",
  taxCode: "",
  companyName: "",
  invoiceEmail: "",
  officeAddress: "",
  companyBankAccount: "",
  personalBankAccount: "",
};

const emptyPasswordForm = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

/** Thông báo lỗi ngắn gọn cho người dùng (không hiển thị dòng Request: POST …). */
const formatUserFacingApiError = (err, fallback = "Có lỗi xảy ra.") => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data && typeof data === "object") {
    const msg = data.message ?? data.error ?? data.detail;
    if (msg != null && String(msg).trim()) return String(msg).trim();
  }
  if (typeof data === "string" && data.trim()) {
    const text = data.trim();
    if (!text.startsWith("<")) {
      try {
        const parsed = JSON.parse(text);
        const fromJson = parsed?.message ?? parsed?.error;
        if (fromJson != null && String(fromJson).trim()) return String(fromJson).trim();
      } catch {
        if (text.length <= 400) return text;
      }
    }
  }

  if (status === 401) {
    return "Phiên đăng nhập không đúng hoặc đã hết hạn. Vui lòng đăng nhập lại.";
  }
  if (status === 403) {
    return "Bạn không có quyền thực hiện thao tác này.";
  }
  if (status === 404) {
    return "Chức năng đổi mật khẩu chưa có trên server (404). Khởi động lại backend rồi thử lại.";
  }
  if (status === 405) {
    return "Server không hỗ trợ đổi mật khẩu theo cách này (405). Khởi động lại backend rồi thử lại.";
  }
  if (status >= 500) {
    return `Lỗi máy chủ (${status}). Vui lòng thử lại sau hoặc liên hệ quản trị viên.`;
  }
  if (!err?.response) {
    if (err?.code === "ERR_NETWORK") {
      return "Không kết nối được máy chủ. Kiểm tra backend đang chạy và kết nối mạng.";
    }
    return err?.message || fallback;
  }
  const statusText = err?.response?.statusText;
  if (status) {
    return `Yêu cầu thất bại (HTTP ${status}${statusText ? ` — ${statusText}` : ""}).`;
  }
  return err?.message || fallback;
};

const extractErrorDetail = (err) => {
  const status = err?.response?.status;
  const statusText = err?.response?.statusText;
  const data = err?.response?.data;
  const requestUrl = err?.config?.url || "";
  const requestMethod = (err?.config?.method || "").toUpperCase();

  let detail = "";
  if (typeof data === "string") {
    detail = data;
  } else if (data && typeof data === "object") {
    if (data.message) detail = String(data.message);
    else if (data.error) detail = String(data.error);
    else if (data.detail) detail = String(data.detail);
    else if (Array.isArray(data.errors) && data.errors.length) {
      detail = data.errors.map((item) => (typeof item === "string" ? item : JSON.stringify(item))).join("\n");
    }
    else {
      try {
        detail = JSON.stringify(data, null, 2);
      } catch {
        detail = String(data);
      }
    }
  } else {
    detail = err?.message || "Có lỗi xảy ra.";
  }

  const header = status ? `HTTP ${status}${statusText ? ` - ${statusText}` : ""}` : "Request failed";
  let diagnostics = "";
  if (requestMethod || requestUrl) {
    diagnostics += `Request: ${requestMethod || "UNKNOWN"} ${requestUrl || "(unknown url)"}`;
  }
  if (status) {
    diagnostics += `${diagnostics ? "\n" : ""}Response: ${status}${statusText ? ` ${statusText}` : ""}`;
  }
  if (!detail || detail.trim() === "" || detail === "Request failed with status code 500") {
    detail = err?.message || "Không nhận được thông tin lỗi chi tiết từ backend.";
  }
  const mergedDetail = diagnostics ? `${diagnostics}\n\n${detail}` : detail;
  return { header, detail: mergedDetail };
};

const isMethodNotSupportedError = (err, method) => {
  const status = err?.response?.status;
  const msgFromBody =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "";
  const fallbackMsg = err?.message || "";
  const text = `${msgFromBody} ${fallbackMsg}`;
  return (
    status === 405 ||
    text.includes(`Request method '${method}' is not supported`) ||
    text.includes(`Request method "${method}" is not supported`)
  );
};

const AdminMyProfile = () => {
  const reduxUser = useSelector((state) => state.user.user);
  const config = general.getLocalStorage("user_config");
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const syncLocalConfig = async () => {
    try {
      const cfgRes = await axiosInstance.get("/api/users/getConfig");
      const cfg = cfgRes?.data?.data;
      if (cfg) {
        general.setLocalStorage("user_config", cfg);
      }
    } catch {
      /* ignore */
    }
  };

  const loadProfile = async () => {
    const res = await axiosInstance.get("/api/users/getProfile");
    const data = res?.data?.data;
    if (data) {
      setProfile(data);
      setForm({
        name: data.name ?? "",
        customerCode: data.customerCode ?? "",
        phoneNumber: data.phoneNumber ?? "",
        taxCode: data.taxCode ?? "",
        companyName: data.companyName ?? "",
        invoiceEmail: data.invoiceEmail ?? "",
        officeAddress: data.officeAddress ?? "",
        companyBankAccount: data.companyBankAccount ?? "",
        personalBankAccount: data.personalBankAccount ?? "",
      });
      setError(null);
    } else {
      setProfile(null);
      setError(res?.data?.message || "Không tải được hồ sơ.");
    }
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Hồ sơ của tôi";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadProfile();
      } catch (e) {
        if (!cancelled) setError("Không tải được hồ sơ. Vui lòng thử lại.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePickAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) {
      if (file) toast.warning("Vui lòng chọn file ảnh.");
      return;
    }
    setAvatarFile(file);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  /** Chỉ bắt mật khẩu hiện tại khi đã có password và không đang được yêu cầu thiết lập lại. */
  const needsCurrentPassword =
    profile?.hasPassword === true && profile?.passwordSetupRequired !== true;
  const isFirstPasswordSetup =
    profile?.passwordSetupRequired === true || profile?.hasPassword !== true;

  const openChangePasswordModal = () => {
    setPasswordForm(emptyPasswordForm);
    setPasswordError("");
    setShowChangePasswordModal(true);
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setPasswordForm(emptyPasswordForm);
    setPasswordError("");
  };

  const handlePasswordFieldChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (passwordError) setPasswordError("");
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!newPassword || !confirmPassword) {
      setPasswordError("Vui lòng nhập đầy đủ mật khẩu mới.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Mật khẩu mới phải có ít nhất 6 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Mật khẩu mới và nhập lại mật khẩu không trùng khớp.");
      return;
    }
    if (needsCurrentPassword && !currentPassword.trim()) {
      setPasswordError("Vui lòng nhập mật khẩu hiện tại.");
      return;
    }
    setChangingPassword(true);
    setPasswordError("");
    try {
      const body = {
        currentPassword: needsCurrentPassword ? currentPassword : undefined,
        newPassword,
      };
      let res;
      try {
        // Ưu tiên PUT — khớp backend (PUT + POST) giống updateMyProfile.
        res = await axiosInstance.put("/api/users/my/changePassword", body, {
          headers: { "Content-Type": "application/json" },
        });
      } catch (putErr) {
        if (isMethodNotSupportedError(putErr, "PUT")) {
          res = await axiosInstance.post("/api/users/my/changePassword", body, {
            headers: { "Content-Type": "application/json" },
          });
        } else {
          throw putErr;
        }
      }
      if (res.data?.success) {
        toast.success(res.data?.message || "Đã đổi mật khẩu thành công.");
        closeChangePasswordModal();
        await loadProfile();
      } else {
        const msg = res.data?.message || "Không đổi được mật khẩu. Vui lòng thử lại.";
        setPasswordError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = formatUserFacingApiError(err, "Không đổi được mật khẩu. Vui lòng thử lại.");
      setPasswordError(msg);
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        ...profile,
        ...form,
        name: form.name.trim(),
      };
      const fd = new FormData();
      fd.append(
        "user",
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
      if (avatarFile) {
        fd.append("avatar", avatarFile);
      }
      let res;
      try {
        // Prefer PUT first because current backend instance supports PUT.
        res = await axiosInstance.put("/api/users/updateMyProfile", fd);
      } catch (putErr) {
        if (isMethodNotSupportedError(putErr, "PUT")) {
          res = await axiosInstance.post("/api/users/updateMyProfile", fd);
        } else {
          throw putErr;
        }
      }
      if (res.data?.success) {
        toast.success(res.data?.message || "Đã lưu thông tin.");
        setAvatarFile(null);
        setAvatarPreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        await loadProfile();
        await syncLocalConfig();
      } else {
        const msg = res.data?.message || "Không lưu được. Vui lòng thử lại.";
        setSaveError({
          header: "Lưu thất bại",
          detail: `Request: PUT /api/users/updateMyProfile\n\n${msg}`,
        });
        toast.error(msg);
      }
    } catch (err) {
      const parsed = extractErrorDetail(err);
      setSaveError(parsed);
      toast.error(`${parsed.header}: ${parsed.detail.split("\n")[0]}`);
    } finally {
      setSaving(false);
    }
  };

  const displayRole = roleLabel(profile?.role || reduxUser?.role, {
    isAdmin: reduxUser?.isAdmin === true,
    isMember: reduxUser?.isMember === true,
  });
  const avatarSrc =
    avatarPreview ||
    resolveAvatarSrc(profile?.picture, config?.userPath);

  return (
    <div className="container-fluid p-0 web-cover">
      <BackendHeader />
      <Container className="py-4">
        <h1 className="admin-my-profile__title mb-4">Hồ sơ của tôi</h1>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        )}
        {!loading && error && <div className="alert alert-danger">{error}</div>}
        {!loading && profile && (
          <Card>
            <Card.Body className="p-0">
              {saveError && (
                <div className="alert alert-danger m-4 mb-0">
                  <div className="fw-semibold mb-1">{saveError.header}</div>
                  <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                    {saveError.detail}
                  </pre>
                </div>
              )}
              <Form onSubmit={handleSubmit}>
                <div className="p-4">
                <div className="d-flex flex-column flex-md-row gap-4 align-items-start mb-4 pb-4 border-bottom">
                  <div className="admin-my-profile__avatar-wrap position-relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={handlePickAvatar}
                    />
                    {avatarSrc ? (
                      <span className="admin-my-profile__avatar-frame">
                        <img src={avatarSrc} alt="" />
                      </span>
                    ) : (
                      <span className="admin-my-profile__avatar-frame">
                        <span className="admin-my-profile__avatar-placeholder text-secondary">
                          {(form.name || profile.email || "?").charAt(0).toUpperCase()}
                        </span>
                      </span>
                    )}
                    <button
                      type="button"
                      className="admin-my-profile__avatar-edit btn btn-light rounded-circle shadow-sm"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Chọn ảnh đại diện"
                      title="Đổi ảnh đại diện"
                    >
                      <FiEdit2 size={16} aria-hidden />
                    </button>
                  </div>
                  <div className="flex-grow-1 pt-md-1">
                    <span className="d-block fs-5 fw-semibold">{form.name || profile.name || "—"}</span>
                    <span className="admin-my-profile__role-badge">{displayRole}</span>
                  </div>
                </div>

                <Row className="g-3 mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Họ và tên</Form.Label>
                      <Form.Control
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        autoComplete="name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" value={profile.email || ""} disabled readOnly />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Số điện thoại</Form.Label>
                      <Form.Control name="phoneNumber" value={form.phoneNumber} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Địa chỉ văn phòng</Form.Label>
                      <Form.Control name="officeAddress" value={form.officeAddress} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-2 pt-2 border-top mt-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Cấp / Bậc</Form.Label>
                      <Form.Control readOnly disabled value={profile.levelName || "—"} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Thứ hạng</Form.Label>
                      <Form.Control
                        readOnly
                        disabled
                        value={profile.userRank != null ? String(profile.userRank) : "—"}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Tuyến vận chuyển</Form.Label>
                      <Form.Control as="textarea" rows={2} readOnly disabled value={profile.lines || ""} />
                    </Form.Group>
                  </Col>
                </Row>

                </div>

                <div className="d-flex justify-content-end mb-1 px-4">
                  <small className="text-muted" style={{ fontSize: "0.72rem", lineHeight: 1.1 }}>
                    Đã tạo lúc {general.formatDateTime2(profile.createdDate)}
                  </small>
                </div>

                <div className="d-flex justify-content-between align-items-center pt-3 px-4 pb-4 border-top">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 admin-my-profile__change-password-link"
                    onClick={openChangePasswordModal}
                  >
                    Thay đổi mật khẩu
                  </button>
                  <Button size="sm" type="submit" variant="primary" disabled={saving} className="d-inline-flex align-items-center gap-2 px-4">
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Đang lưu…
                      </>
                    ) : (
                      <>
                        <FiSave size={18} aria-hidden />
                        Lưu
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}
      </Container>

      <Modal
        show={showChangePasswordModal}
        onHide={closeChangePasswordModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Thay đổi mật khẩu</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleChangePasswordSubmit}>
          <Modal.Body>
            {isFirstPasswordSetup && (
              <p className="text-muted small mb-3">
                Tài khoản chưa có mật khẩu đăng nhập (ví dụ đăng nhập bằng Google). Vui lòng thiết lập mật khẩu mới; không cần mật khẩu hiện tại.
              </p>
            )}
            {passwordError && <div className="alert alert-danger py-2 small">{passwordError}</div>}
            <Form.Group className="mb-3">
              <Form.Label>Tên đăng nhập</Form.Label>
              <Form.Control
                type="text"
                value={profile?.username || profile?.email || "—"}
                readOnly
                disabled
              />
            </Form.Group>
            {needsCurrentPassword && (
              <Form.Group className="mb-3">
                <Form.Label>Mật khẩu hiện tại</Form.Label>
                <Form.Control
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordFieldChange}
                  autoComplete="current-password"
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Mật khẩu mới</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordFieldChange}
                autoComplete="new-password"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Nhập lại mật khẩu mới</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordFieldChange}
                autoComplete="new-password"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="justify-content-end">
            <Button
              size="sm"
              variant="primary"
              type="submit"
              disabled={changingPassword}
              className="d-inline-flex align-items-center gap-2"
            >
              {changingPassword ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Đang lưu…
                </>
              ) : (
                <>
                  <FiSave size={16} aria-hidden />
                  Lưu mật khẩu
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminMyProfile;
